import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LANGUAGES,
  checkLanguageConfiguration,
  type LanguageCode,
  type LanguageConfiguration,
  type SwitchRule,
} from "@/config/language-rules";
import { AlertTriangle } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useLanguageConflicts } from "../languageSetup";

// What the form holds while the user is still picking: a multilingual value with fewer than 2
// languages. checkLanguageConfiguration reads it as null, so nothing filters; the form schema
// refuses to save it.
interface Draft {
  mode: "multilingual";
  languages: LanguageCode[];
  primary: LanguageCode;
  switch_rule: SwitchRule;
}

const isDraft = (raw: any): raw is Draft =>
  !!raw && raw.mode === "multilingual" && Array.isArray(raw.languages);

export default function LanguageTab() {
  const { watch, setValue } = useFormContext();
  const raw = watch("language_configuration");
  const conflicts = useLanguageConflicts();

  const valid = checkLanguageConfiguration(raw);
  const current: LanguageConfiguration | Draft | null = valid ?? (isDraft(raw) ? raw : null);

  const update = (next: LanguageConfiguration | Draft | null) =>
    setValue("language_configuration", next, { shouldDirty: true, shouldValidate: true });

  const setSingle = (code: LanguageCode) =>
    update({ mode: "single", languages: [code], primary: code, switch_rule: "ask" });

  const toMultilingual = () => {
    const keep = current?.primary ?? "en";
    const languages: LanguageCode[] = current?.languages?.length ? [...current.languages] : [keep];
    update({
      mode: "multilingual",
      languages,
      primary: keep,
      switch_rule: current?.switch_rule ?? "ask",
    });
  };

  const toggleLanguage = (code: LanguageCode, on: boolean) => {
    if (!current) return;
    const picked = on
      ? LANGUAGES.map((l) => l.code).filter((c) => c === code || current.languages.includes(c))
      : current.languages.filter((c) => c !== code);
    // The primary must be a chosen language: when it is unticked, take the first chosen one.
    const primary = picked.includes(current.primary) ? current.primary : picked[0] ?? current.primary;
    update({
      mode: "multilingual",
      languages: picked,
      primary,
      switch_rule: current.switch_rule,
    });
  };

  if (!current) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Not set. The assistant has no language rule, and the transcriber and voice
          lists show everything.
        </p>
        <Button type="button" variant="outline" onClick={() => setSingle("en")}>
          Set a language
        </Button>
      </div>
    );
  }

  const multilingual = current.mode === "multilingual";
  const tooFew = multilingual && current.languages.length < 2;

  return (
    <div className="space-y-4">
      {conflicts.length > 0 && (
        <Alert variant="destructive" className="border-destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-1">This language setting does not fit the chosen providers.</p>
            <ul className="list-disc pl-4 space-y-1">
              {conflicts.map((c) => (
                <li key={c.message}>{c.message}</li>
              ))}
            </ul>
            <p className="mt-1">Saving is blocked until this is fixed.</p>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-end justify-between gap-4">
        <div className="space-y-2 w-60">
          <Label>Mode</Label>
          <Select
            value={current.mode}
            onValueChange={(v) => (v === "single" ? setSingle(current.primary) : toMultilingual())}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single language</SelectItem>
              <SelectItem value="multilingual">Multilingual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="ghost" onClick={() => update(null)}>
          Clear
        </Button>
      </div>

      {!multilingual && (
        <div className="space-y-2 w-60">
          <Label>Language</Label>
          <Select value={current.primary} onValueChange={(v) => setSingle(v as LanguageCode)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {multilingual && (
        <>
          <div className="space-y-2">
            <Label>Languages the assistant speaks (pick 2 or more)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {LANGUAGES.map((l) => {
                const id = `language-${l.code}`;
                return (
                  <div key={l.code} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={current.languages.includes(l.code)}
                      onCheckedChange={(on) => toggleLanguage(l.code, on === true)}
                    />
                    <Label htmlFor={id} className="font-normal">
                      {l.name}
                    </Label>
                  </div>
                );
              })}
            </div>
            {tooFew && (
              <p className="text-sm text-destructive">
                Pick at least 2 languages. Saving is blocked until you do.
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Primary language</Label>
              <Select
                value={current.languages.includes(current.primary) ? current.primary : undefined}
                onValueChange={(v) =>
                  update({ ...current, primary: v as LanguageCode, mode: "multilingual" })
                }
                disabled={current.languages.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.filter((l) => current.languages.includes(l.code)).map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">The call starts in this language.</p>
            </div>

            <div className="space-y-2">
              <Label>Switching</Label>
              <Select
                value={current.switch_rule}
                onValueChange={(v) =>
                  update({ ...current, switch_rule: v as SwitchRule, mode: "multilingual" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ask">Only when asked</SelectItem>
                  <SelectItem value="follow">Follow the caller</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Only when asked": the assistant changes language only when the caller asks for
                one. "Follow the caller": it also changes after the caller speaks another listed
                language for 2 turns in a row.
              </p>
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        English is always available when a caller asks for it. Replies in a language other
        than English are written in its own script, with English words mixed in. V2 calls only.
      </p>
    </div>
  );
}
